import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import {
  Observable,
  catchError,
  of,
  switchMap,
  map,
  take,
  throwError,
  timeout,
} from 'rxjs';
import { isInMsalSilentIframe } from '@ups/xplat/web/utils';
import { AuthFacade } from './auth-facade';
import { ENTRA_LOGIN_CONFIG } from './dual-login/dual-login.ui-service';
import {
  TOKEN_ACQUIRE_BOUND_MS,
  TokenAcquireError,
} from './token-acquire-bound';
import {
  AUTH_RETRY_HEADER,
  interceptAuth0,
} from './issuer-aware-auth.auth0-branch';

/**
 * MSAL reports "there is no signed-in account at all" as this error code.
 * It is categorically different from a refresh that failed: no retry, no
 * 401 round trip and no amount of waiting can produce a token, because
 * there is no session to renew. Matched on the code rather than the
 * message so a wording change upstream cannot silently un-match it.
 */
function isNoAccountError(err: unknown): boolean {
  const code = (err as { errorCode?: unknown } | null)?.errorCode;
  return code === 'no_account_error';
}

/**
 * Browser-side silent-renewal failures that, like an interaction-required error,
 * can ONLY be recovered by an interactive redirect — MSAL raises them as
 * `BrowserAuthError`, not `InteractionRequiredAuthError`, so they slip past an
 * interaction-required-only match and left an expired session proceeding
 * unauthenticated instead of recovering (#31904). All mean "silent renewal
 * cannot run or produced no token", never a transient blip. msal-browser v5
 * codes (verified against the installed package): `block_iframe_reload` (no
 * cached refresh token, iframe renewal blocked because the redirect URI is the
 * app), `timed_out` (silent iframe needed interaction), `no_tokens_found` (an
 * account but no usable cached token).
 */
const INTERACTIVE_RECOVERABLE_BROWSER_CODES = new Set([
  'block_iframe_reload',
  'timed_out',
  'no_tokens_found',
]);

/**
 * True when a silent token acquisition failed in a way that ONLY an
 * interactive redirect can recover — the user must sign in interactively.
 * Covers two MSAL error families: the canonical interaction-required set
 * ({@link InteractionRequiredAuthError} / `interaction_required` /
 * `login_required` / `consent_required` — expired/revoked refresh token,
 * lapsed SSO, new consent or conditional-access) AND the browser silent-
 * renewal failures in {@link INTERACTIVE_RECOVERABLE_BROWSER_CODES}. In every
 * case, waiting or retrying silently can NEVER produce a token. Matched on the
 * error type and on error codes so a wording change upstream cannot silently
 * un-match it. Deliberately does NOT match `no_account_error` (no session to
 * recover — handled separately) or transient/network failures (a later call
 * succeeds, so a jarring full-page redirect would be wrong).
 */
function needsInteractiveRecovery(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) {
    return true;
  }
  const code = (err as { errorCode?: unknown } | null)?.errorCode;
  return (
    code === 'interaction_required' ||
    code === 'login_required' ||
    code === 'consent_required' ||
    (typeof code === 'string' &&
      INTERACTIVE_RECOVERABLE_BROWSER_CODES.has(code))
  );
}

/**
 * Issuer-aware implementation of the HTTP_AUTH_INTERCEPTOR seam (#29709).
 * MyHttpClientFactory takes its auth interceptor by injection token, so binding
 * this class routes every factory-built client by session issuer: Entra gets a
 * Bearer token from {@link AuthFacade} (MSAL silent refresh); Auth0 DELEGATES to
 * the stock Auth0 AuthHttpInterceptor so allowedList + refresh semantics stay
 * byte-identical for every pre-existing user.
 *
 * Token-failure handling is not one-size-fits-all: a transient failure proceeds
 * unauthenticated (the API's 401 is the re-login signal), `no_account_error`
 * fails loudly without sending (#30531), and a session that can no longer be
 * renewed silently triggers an interactive redirect (#31904). Both branches also
 * recover a 401 after a token was attached with one forced refresh + replay
 * (#31536 Auth0, #31749/#31904 Entra); see {@link interceptEntra} and
 * {@link interceptAuth0} for the per-issuer decision tables.
 */
@Injectable()
export class IssuerAwareAuthInterceptor implements HttpInterceptor {
  private readonly facade = inject(AuthFacade);
  private readonly injector = inject(Injector);
  // Same injected config EntraAuthenticationService reads (default factory:
  // environment.entraId), so the interceptor's own MSAL calls request the exact
  // scopes the primary token flow does — a single source, provably no drift.
  private readonly entraConfig = inject(ENTRA_LOGIN_CONFIG);

  /**
   * Latches interactive recovery to at most ONE attempt per page load. A
   * single expired session typically 401s several concurrent background calls
   * (permission load, userProfile, feature checks) in the same tick; without
   * this each would call `acquireTokenRedirect`, and MSAL rejects the 2nd..Nth
   * with `interaction_in_progress`. The first request wins and navigates the
   * document to Microsoft; the rest abort quietly. NOT reset on a failed
   * launch: if the redirect cannot start (the block_iframe_reload config issue,
   * or interaction already in progress) retrying cannot help and would spin a
   * console-flooding loop. A full page load starts fresh with the latch clear.
   */
  private redirectInFlight = false;

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    switch (this.facade.issuer) {
      case 'entra':
        return this.interceptEntra(req, next);
      case 'auth0':
        return interceptAuth0(req, next, {
          injector: this.injector,
          triggerRedirect: () => this.triggerAuth0Redirect(),
        });
      default:
        // 'internal' (employee InternalAuth) is neither an Entra
        // nor an Auth0 session: forging an Auth0 acquire would fail with
        // login_required and bounce the user to Auth0. AuthFacade never
        // selects it today (selectLoginIssuer returns entra|auth0), so this
        // is a fail-safe: pass through untouched and leave the Bearer to the
        // internal-auth interceptor where an app wires one.
        return next.handle(req);
    }
  }

  /**
   * Entra path. Three distinct token outcomes, three answers (#31904 — Entra
   * parity with the Auth0 branch's #31536 recovery; the recovery half was the
   * gap that stranded an expired Entra session in an unbreakable 401 loop with
   * no route back to Microsoft — the symptom that motivated this):
   *
   * 1. **`no_account_error`** — no signed-in account at all. Fail loudly and
   *    do NOT send the request (see {@link isNoAccountError}); an anonymous
   *    call flattens downstream into a misleading empty-permissions state.
   * 2. **Silent renewal cannot produce a token** — a real session that can no
   *    longer be renewed silently: `InteractionRequiredAuthError` (expired/
   *    revoked refresh token, lapsed SSO, new consent/conditional-access) OR a
   *    browser silent-renewal failure ({@link needsInteractiveRecovery}, e.g.
   *    `block_iframe_reload` when no refresh token is cached). Silent retries
   *    can never succeed, so the ONLY recovery is an interactive redirect;
   *    trigger it and abort the request (the page is about to navigate away).
   *    This is the fix for "the app can't recover when the Entra login expires".
   * 3. **Any other silent failure** — treated as transient: proceed
   *    unauthenticated so the API's 401 remains the re-login signal, exactly
   *    as before.
   *
   * Once a token IS attached, a 401 from the API is still recoverable: force
   * a non-cached silent refresh and replay once ({@link forceRefreshEntraAndRetry}),
   * mirroring the Auth0 branch's overnight-token recovery.
   */
  private interceptEntra(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.facade.getAccessToken().pipe(
      // #32087: MSAL bounds its iframe renewal but not its token-endpoint
      // fetch, so cap the whole acquire. A timeout is treated as TRANSIENT
      // (case 3 below): proceed unauthenticated and let the API's 401 drive
      // the bounded forced refresh, so the request can never hang unsent.
      timeout({
        first: TOKEN_ACQUIRE_BOUND_MS,
        with: () => throwError(() => new TokenAcquireError('entra', 'timeout')),
      }),
      map((token) =>
        req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      ),
      catchError((err) => {
        if (isNoAccountError(err)) {
          console.error(
            '[auth] no signed-in Entra account; this session cannot be authenticated. ' +
              'Re-authentication is required. Request NOT sent unauthenticated.',
            err
          );
          return throwError(
            () =>
              new Error(
                'No signed-in Entra account: the request was not sent. Sign in again.'
              )
          );
        }
        if (needsInteractiveRecovery(err)) {
          console.warn(
            '[auth] Entra session can no longer be renewed silently; ' +
              'starting interactive re-authentication.',
            err
          );
          this.triggerEntraRedirect();
          return throwError(
            () => new Error('Entra session expired; redirecting to sign in.')
          );
        }
        console.warn(
          err instanceof TokenAcquireError
            ? `[auth] silent Entra token acquisition did not settle within ${TOKEN_ACQUIRE_BOUND_MS}ms; request proceeds unauthenticated`
            : '[auth] silent Entra token acquisition failed; request proceeds unauthenticated',
          err
        );
        return of(req);
      }),
      switchMap((request) =>
        next.handle(request).pipe(
          catchError((err: unknown) => {
            const alreadyRetried = req.headers.get(AUTH_RETRY_HEADER) === '1';
            if (
              alreadyRetried ||
              !(err instanceof HttpErrorResponse) ||
              err.status !== 401
            ) {
              return throwError(() => err);
            }
            return this.forceRefreshEntraAndRetry(req, next, err);
          })
        )
      )
    );
  }

  /**
   * A token was attached but the API answered 401 — the access token is stale
   * (issued before a role/scope change, or a cached token MSAL still considers
   * live). Force a NON-cached refresh via `AuthFacade.getAccessToken(true)`
   * (#31749 — the same forced-acquire the Auth0 branch uses; load-bearing
   * because a plain silent call returns the same token that just 401'd, and
   * routing through the facade reuses the AuthenticationService's scopes and
   * MSAL-init gating) and replay the request exactly once. If the forced
   * refresh itself reports
   * interaction is required OR that there is no signed-in account (the session
   * died between the successful token attach and this 401 — e.g. a sign-out in
   * another tab), the session is dead — escalate to an interactive redirect.
   * Either way the ORIGINAL 401 surfaces if recovery fails, never a loop (the
   * retry header caps this at one attempt).
   */
  private forceRefreshEntraAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    originalErr: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    return this.facade.getAccessToken(true).pipe(
      // #32087: a forced refresh is a second acquire and can stall like the
      // first; at the bound the catchError below surfaces the ORIGINAL 401.
      timeout({
        first: TOKEN_ACQUIRE_BOUND_MS,
        with: () => throwError(() => new TokenAcquireError('entra', 'timeout')),
      }),
      take(1),
      switchMap((token) => {
        const retried = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            [AUTH_RETRY_HEADER]: '1',
          },
        });
        return next.handle(retried);
      }),
      catchError((refreshErr: unknown) => {
        // no_account_error here is the mid-session variant of the primary
        // path's fail-loud case (#30531): the account vanished after the
        // token was attached. Escalate to an interactive redirect rather
        // than silently resurfacing the stale 401, which would be a narrower
        // version of the very bug this PR fixes.
        if (
          needsInteractiveRecovery(refreshErr) ||
          isNoAccountError(refreshErr)
        ) {
          this.triggerEntraRedirect();
        }
        return throwError(() => originalErr);
      })
    );
  }

  /**
   * Kick off MSAL's interactive redirect to re-establish the session, guarded
   * so concurrent 401s fire it exactly once (see {@link redirectInFlight}).
   * The redirect navigates the document to Microsoft and back, rebooting the
   * app with a fresh session — the same recovery `MsalGuard` performs on
   * navigation, made available to background API calls that never navigate.
   */
  private triggerEntraRedirect(): void {
    if (this.redirectInFlight) {
      return;
    }
    // Never redirect from inside a hidden iframe. MSAL's silent-renewal iframe
    // loads this SPA at the app-origin redirectUri, so the app can boot and run
    // this interceptor *inside* that iframe; MSAL refuses a redirect there
    // (`block_iframe_reload`), and redirecting the iframe would be futile
    // anyway. Only the top window drives the recovery. Shares the exact
    // predicate the app-bootstrap guard uses, so "don't bootstrap here" and
    // "don't redirect here" can never disagree (review finding #31904).
    if (isInMsalSilentIframe()) {
      return;
    }
    // Latch for the whole page load: attempt the redirect at most once. If it
    // fails to START (e.g. the underlying block_iframe_reload config issue, or
    // interaction already in progress), retrying cannot help and would spin a
    // console-flooding loop as each queued 401 re-enters. The next full page
    // load resets the latch and tries again from a clean state.
    this.redirectInFlight = true;
    this.injector
      .get(MsalService)
      .acquireTokenRedirect({ scopes: this.entraScopes() })
      .subscribe({
        error: (err) => {
          console.error(
            '[auth] Entra interactive re-authentication failed to start; ' +
              'not retrying this page load',
            err
          );
        },
      });
  }

  /**
   * Scopes the interceptor's own MSAL calls (force-refresh + interactive
   * redirect) request. Read from {@link ENTRA_LOGIN_CONFIG} — the SAME injected
   * config `EntraAuthenticationService.getAccessTokenSilently` uses — so the
   * recovery calls and the primary token flow always request identical scopes
   * and cannot drift. `.default` resolves to every statically-consented
   * permission on the app registration (a superset of any explicit scope list),
   * which is what the two apps wiring this interceptor
   * (web-hr-packet, web-software-security) grant. Review finding #31904.
   */
  private entraScopes(): string[] {
    const audience = this.entraConfig?.audience;
    return audience ? [`${audience}/.default`] : [];
  }

  /**
   * Auth0 mirror of {@link triggerEntraRedirect} (#32087): re-establish the
   * session interactively. Shares the {@link redirectInFlight} latch so N
   * concurrent expired-session failures in one tick fire ONE redirect (auth0-
   * spa-js rejects a second interactive call while one is in progress); safe to
   * share because a session is single-issuer. No hidden-iframe guard is needed:
   * auth0-spa-js runs its silent `/authorize` against the Auth0 domain, not the
   * app origin, so this interceptor never runs inside an Auth0 renewal iframe.
   */
  private triggerAuth0Redirect(): void {
    if (this.redirectInFlight) {
      return;
    }
    this.redirectInFlight = true;
    this.injector
      .get(AuthService)
      .loginWithRedirect()
      .subscribe({
        error: (err) => {
          console.error(
            '[auth] Auth0 interactive re-authentication failed to start; ' +
              'not retrying this page load',
            err
          );
        },
      });
  }
}
